// A EAP completa da obra na tela de Desembolso, com a distribuição do desembolso mês a
// mês no período selecionado — a mesma grade da Projeção Financeira, com a ação de
// editar a regra de cada nó.
//
// Reaproveita os MESMOS carregadores da Projeção (`loadUnits`/`loadCells`/…, via
// `loadProjectionBreakdown`): eles já resolvem corrida de requisições, cache por escopo
// e recorte pelo período do contexto (`filterDates`). Nada de rota nova, e as duas telas
// leem exatamente a mesma árvore.
import { useEffect, useMemo } from "react";

import { useProjectActions, useProjectContext } from "@contexts/projects";

import ProjectionLoadState from "../../financialProjection/ProjectionLoadState";
import monthsBetween from "../months";
import { contributionOf } from "../slice";
import CellAccordion from "./CellAccordion";
import EapRuleRow from "./EapRuleRow";
import { EapTableContext } from "./context";

// Unidades sem apropriação não têm filhos para abrir — a Projeção Financeira já trata
// esse caso assim, e repetir a regra aqui mantém as duas árvores iguais.
const UNALLOCATED_UNIT = "ITENS SEM APROPRIAÇÃO";

const NO_MONTHS = new Set();

const EapTable = ({
  readOnly = false,
  rulesByNodeKey = {},
  onEditRule,
  selectedMonths = NO_MONTHS,
  onSliceAdjust = null,
  followsLabel = null,
  distributionEditor = null,
}) => {
  const { activeProject, filterDates } = useProjectContext();
  const { loadUnits } = useProjectActions();
  const workId = activeProject?._id;
  const units = activeProject?.units;

  useEffect(() => {
    if (workId) loadUnits();
  }, [workId, filterDates]);

  const months = useMemo(
    () => monthsBetween(filterDates?.beginAt, filterDates?.endAt),
    [filterDates?.beginAt, filterDates?.endAt],
  );

  // Uma obra com mais de uma raiz de EAP vem com as unidades misturadas: agrupar por
  // `wbs_code_id_building` mantém cada centro de custo no seu bloco.
  const unitGroups = useMemo(() => {
    if (!Array.isArray(units)) return [];
    const byBuilding = units.reduce((groups, unit) => {
      const key = String(unit?.wbs_code_id_building ?? "");
      groups.set(key, [...(groups.get(key) ?? []), unit]);
      return groups;
    }, new Map());
    return [...byBuilding.entries()];
  }, [units]);

  const value = useMemo(
    () => ({
      readOnly, rulesByNodeKey, onEditRule, selectedMonths, onSliceAdjust, followsLabel,
      distributionEditor,
    }),
    [
      readOnly, rulesByNodeKey, onEditRule, selectedMonths, onSliceAdjust, followsLabel,
      distributionEditor,
    ],
  );

  // Com meses selecionados só entram as unidades com valor neles. Se as unidades já chegaram
  // e nenhuma tem, a área declara que nenhum item compõe o mês — nunca mostra a EAP completa.
  const sliceActive = selectedMonths.size > 0;
  const sliceEmpty =
    sliceActive &&
    Array.isArray(units) &&
    !units.some(
      (unit) =>
        contributionOf(unit?.monthlyProjection?.ampliDisbursement, selectedMonths) > 0,
    );

  const multipleRoots = unitGroups.length > 1;

  return (
    <EapTableContext.Provider value={value}>
      <section className="ampli-eap-table" aria-label="EAP do empreendimento">
        <h3>EAP do empreendimento</h3>
        <p>
          O desembolso de cada nível da EAP no período selecionado. Expanda até
          o serviço e defina a forma de pagar onde fizer sentido — a regra de um
          nível vale para tudo abaixo dele, até ser sobrescrita.
        </p>
        {sliceActive ? (
          <p className="card-label">
            {`Itens com desembolso em ${
              selectedMonths.size === 1 ? "o mês selecionado" : "algum dos meses selecionados"
            }; o valor de cada um é a soma desses meses.`}
          </p>
        ) : null}
        {sliceEmpty ? (
          <p className="empty-state" role="status">
            Nenhum item compõe o desembolso do mês selecionado.
          </p>
        ) : null}
        {months.length === 0 ? (
          <p className="empty-state">
            Escolha um período para ver a distribuição do desembolso.
          </p>
        ) : null}
        <div className="projection-table">
          <div className="accordion margin-top-24">
            <ProjectionLoadState
              level="units"
              isMissing={!units}
              onRetry={() => loadUnits()}
            />
            {unitGroups.map(([building, groupUnits]) => (
              <div key={building}>
                {multipleRoots ? (
                  <div className="wbs-accordion-divider flex gap-4">
                    <h2>{building}</h2>
                  </div>
                ) : null}
                {groupUnits.map((item) => (
                  <EapRuleRow
                    key={item.building_unit_id ?? item.wbs_code_id}
                    item={item}
                    type="unit"
                    typeTitle="Unidade construtiva"
                    months={months}
                    ChildAccordionComponent={
                      item.building_unit_name === UNALLOCATED_UNIT
                        ? null
                        : CellAccordion
                    }
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>
    </EapTableContext.Provider>
  );
};

export default EapTable;
